import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ValuesProvider } from './contexts/ValuesContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProgressProvider } from './contexts/UserProgressContext';
import ScrollToTop from './components/ScrollToTop';
import BackgroundWrapper from './components/BackgroundWrapper';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationCardManager from './components/NotificationCardManager';
import FloatingThemeToggle from './components/FloatingThemeToggle';
import './App.css';

// Loading component for lazy loaded routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--bg-primary)' }}>
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-t-2" 
           style={{ borderColor: 'var(--primary-blue)' }}></div>
      <p className="mt-4 text-lg" style={{ color: 'var(--text-secondary)' }}>Loading...</p>
    </div>
  </div>
);

// Eager load critical pages (auth, dashboard)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';

// Lazy load non-critical pages for better initial load performance
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const SetupPage = lazy(() => import('./pages/SetupPage'));
const PlanBuilderPage = lazy(() => import('./pages/PlanBuilderPage'));
const SavedPlansPage = lazy(() => import('./pages/SavedPlansPage'));
const SavedThoughtFightingPage = lazy(() => import('./pages/SavedThoughtFightingPage'));
const ActivationPage = lazy(() => import('./pages/ActivationPage'));
const ReflectionPage = lazy(() => import('./pages/ReflectionPage'));
const ExercisePage = lazy(() => import('./pages/ExercisePage'));
const ValuesPage = lazy(() => import('./pages/ValuesPage'));
const GroundingToolkitPage = lazy(() => import('./pages/GroundingToolkitPage'));
const BodyAwarenessPage = lazy(() => import('./pages/BodyAwarenessPage'));
const TriggerLibraryPage = lazy(() => import('./pages/TriggerLibraryPage'));
const CommunicationBoundariesPage = lazy(() => import('./pages/CommunicationBoundariesPage'));
const NarcissistTypesPage = lazy(() => import('./pages/NarcissistTypesPage'));
const NarcissismInsightsPage = lazy(() => import('./pages/NarcissismInsightsPage'));
const NarcissismAtWorkPage = lazy(() => import('./pages/NarcissismAtWorkPage'));
const RolesInAbusiveSystemsPage = lazy(() => import('./pages/RolesInAbusiveSystemsPage'));
const ImpactOnTargetsPage = lazy(() => import('./pages/ImpactOnTargetsPage'));
const RespondingToWorkplaceAbusePage = lazy(() => import('./pages/RespondingToWorkplaceAbusePage'));
const EvaluatingWorkplacePage = lazy(() => import('./pages/EvaluatingWorkplacePage'));
const SurvivorChallengesPage = lazy(() => import('./pages/SurvivorChallengesPage'));
const RelationshipQuizPage = lazy(() => import('./pages/RelationshipQuizPage'));
const NarcissisticParentQuizPage = lazy(() => import('./pages/NarcissisticParentQuizPage'));
const NarcissisticTraitsQuizPage = lazy(() => import('./pages/NarcissisticTraitsQuizPage'));
const FamilyDynamicsPage = lazy(() => import('./pages/FamilyDynamicsPage'));
const CodependencyPage = lazy(() => import('./pages/CodependencyPage'));
const InsightsHubPage = lazy(() => import('./pages/InsightsHubPage'));
const QuizzesHubPage = lazy(() => import('./pages/QuizzesHubPage'));
const BeliefHealthQuizPage = lazy(() => import('./pages/BeliefHealthQuizPage'));
const CoreBeliefsPage = lazy(() => import('./pages/CoreBeliefsPage'));
const BoundaryQuizPage = lazy(() => import('./pages/BoundaryQuizPage'));
const SafetyVsBoundariesQuizPage = lazy(() => import('./pages/SafetyVsBoundariesQuizPage'));
const QuizzesPage = lazy(() => import('./pages/QuizzesPage'));
const NormalizationPatternsPage = lazy(() => import('./pages/NormalizationPatternsPage'));
const TraumaBrainPage = lazy(() => import('./pages/TraumaBrainPage'));
const TraumaBondingPage = lazy(() => import('./pages/TraumaBondingPage'));
const PostSeparationPage = lazy(() => import('./pages/PostSeparationPage'));
const HealthyRelationshipsPage = lazy(() => import('./pages/HealthyRelationshipsPage'));
const CutoffGuidancePage = lazy(() => import('./pages/CutoffGuidancePage'));
const DivorceMisconceptionsPage = lazy(() => import('./pages/DivorceMisconceptionsPage'));
const ThoughtFightingPage = lazy(() => import('./pages/ThoughtFightingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LegalPrivacyPage = lazy(() => import('./pages/LegalPrivacyPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const HighlightsSummaryPage = lazy(() => import('./pages/HighlightsSummaryPage'));
const PricingSelectionPage = lazy(() => import('./pages/PricingSelectionPage'));
const AbuseDefinitionPage = lazy(() => import('./pages/AbuseDefinitionPage'));
const BurnoutRecoveryPage = lazy(() => import('./pages/BurnoutRecoveryPage'));
const BurnoutQuizPage = lazy(() => import('./pages/BurnoutQuizPage'));
const TraumaScreeningQuizPage = lazy(() => import('./pages/TraumaScreeningQuizPage'));
const WorkplaceDynamicsQuizPage = lazy(() => import('./pages/WorkplaceDynamicsQuizPage'));
const TriggersVsThreatsPage = lazy(() => import('./pages/TriggersVsThreatsPage'));
const RealizingWhatYouGrewUpWithPage = lazy(() => import('./pages/RealizingWhatYouGrewUpWithPage'));
const GriefAndLossPage = lazy(() => import('./pages/GriefAndLossPage'));
const ReparentingInPracticePage = lazy(() => import('./pages/ReparentingInPracticePage'));
const WindowOfTolerancePage = lazy(() => import('./pages/WindowOfTolerancePage'));
const GuidedModePage = lazy(() => import('./pages/GuidedModePage'));
const AnxietyBodySUDSPage = lazy(() => import('./pages/AnxietyBodySUDSPage'));
const NarcissisticParentTeenGuidePage = lazy(() => import('./pages/NarcissisticParentTeenGuidePage'));
const SUDSChartBuilderPage = lazy(() => import('./pages/SUDSChartBuilderPage'));
const SUDSSummaryPage = lazy(() => import('./pages/SUDSSummaryPage'));
const BodyMapPage = lazy(() => import('./pages/BodyMapPage'));
const SignalsDictionaryPage = lazy(() => import('./pages/SignalsDictionaryPage'));
const OverLabelingPage = lazy(() => import('./pages/OverLabelingPage'));
const ModuleAdminPage = lazy(() => import('./pages/ModuleAdminPage'));
const ModuleFormPage = lazy(() => import('./pages/ModuleFormPage'));
const ModuleReorderPage = lazy(() => import('./pages/ModuleReorderPage'));
const SectionManagerPage = lazy(() => import('./pages/SectionManagerPage'));
const DynamicModulePage = lazy(() => import('./pages/DynamicModulePage'));
const WallpaperGeneratorPage = lazy(() => import('./pages/WallpaperGenerator'));
const WhereToStartQuizPage = lazy(() => import('./pages/WhereToStartQuizPage'));
const AdminNotificationCardsPage = lazy(() => import('./pages/AdminNotificationCardsPage'));
const AdminInviteLinksPage = lazy(() => import('./pages/AdminInviteLinksPage'));
const AdminThemeCustomizerPage = lazy(() => import('./pages/AdminThemeCustomizerPage'));
const AboutCalmGuardPage = lazy(() => import('./pages/AboutCalmGuardPage'));

// Protected Route Component
function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-gradient)' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p style={{ color: 'var(--text-primary)' }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  return currentUser ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <ThemeProvider>
          <BackgroundWrapper>
            <AuthProvider>
              <UserProgressProvider>
                <ValuesProvider>
                  <BrowserRouter>
                    <ScrollToTop />
                    <FloatingThemeToggle />
                    <NotificationCardManager>
                    <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/about" element={<AboutCalmGuardPage />} />
                
                {/* Protected routes */}
                <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
                <Route path="/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
                <Route path="/plan-builder" element={<ProtectedRoute><PlanBuilderPage /></ProtectedRoute>} />
                <Route path="/saved-plans" element={<ProtectedRoute><SavedPlansPage /></ProtectedRoute>} />
                <Route path="/saved-thought-fighting" element={<ProtectedRoute><SavedThoughtFightingPage /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/activation/:planId" element={<ProtectedRoute><ActivationPage /></ProtectedRoute>} />
                <Route path="/reflection/:planId" element={<ProtectedRoute><ReflectionPage /></ProtectedRoute>} />
                <Route path="/exercise/:type" element={<ProtectedRoute><ExercisePage /></ProtectedRoute>} />
                <Route path="/values" element={<ProtectedRoute><ValuesPage /></ProtectedRoute>} />
                <Route path="/grounding-toolkit" element={<ProtectedRoute><GroundingToolkitPage /></ProtectedRoute>} />
                <Route path="/body-awareness" element={<ProtectedRoute><BodyAwarenessPage /></ProtectedRoute>} />
                <Route path="/trigger-library" element={<ProtectedRoute><TriggerLibraryPage /></ProtectedRoute>} />
                <Route path="/communication-boundaries" element={<ProtectedRoute><CommunicationBoundariesPage /></ProtectedRoute>} />
                <Route path="/narcissist-types" element={<ProtectedRoute><NarcissistTypesPage /></ProtectedRoute>} />
                <Route path="/narcissism-insights" element={<ProtectedRoute><NarcissismInsightsPage /></ProtectedRoute>} />
                <Route path="/narcissism-at-work" element={<ProtectedRoute><NarcissismAtWorkPage /></ProtectedRoute>} />
                <Route path="/roles-abusive-systems" element={<ProtectedRoute><RolesInAbusiveSystemsPage /></ProtectedRoute>} />
                <Route path="/impact-on-targets" element={<ProtectedRoute><ImpactOnTargetsPage /></ProtectedRoute>} />
                <Route path="/responding-workplace-abuse" element={<ProtectedRoute><RespondingToWorkplaceAbusePage /></ProtectedRoute>} />
                <Route path="/evaluating-workplace" element={<ProtectedRoute><EvaluatingWorkplacePage /></ProtectedRoute>} />
                <Route path="/realizing-what-you-grew-up-with" element={<ProtectedRoute><RealizingWhatYouGrewUpWithPage /></ProtectedRoute>} />
                <Route path="/grief-and-loss" element={<ProtectedRoute><GriefAndLossPage /></ProtectedRoute>} />
                <Route path="/reparenting-in-practice" element={<ProtectedRoute><ReparentingInPracticePage /></ProtectedRoute>} />
                <Route path="/survivor-challenges" element={<ProtectedRoute><SurvivorChallengesPage /></ProtectedRoute>} />
                <Route path="/relationship-quiz" element={<ProtectedRoute><RelationshipQuizPage /></ProtectedRoute>} />
                <Route path="/narcissistic-parent-quiz" element={<ProtectedRoute><NarcissisticParentQuizPage /></ProtectedRoute>} />
                <Route path="/narcissistic-traits-quiz" element={<ProtectedRoute><NarcissisticTraitsQuizPage /></ProtectedRoute>} />
                <Route path="/family-dynamics" element={<ProtectedRoute><FamilyDynamicsPage /></ProtectedRoute>} />
                <Route path="/codependency" element={<ProtectedRoute><CodependencyPage /></ProtectedRoute>} />
                <Route path="/insights" element={<ProtectedRoute><InsightsHubPage /></ProtectedRoute>} />
                <Route path="/quizzes" element={<ProtectedRoute><QuizzesHubPage /></ProtectedRoute>} />
                <Route path="/belief-health-quiz" element={<ProtectedRoute><BeliefHealthQuizPage /></ProtectedRoute>} />
                <Route path="/core-beliefs" element={<ProtectedRoute><CoreBeliefsPage /></ProtectedRoute>} />
                <Route path="/boundary-quiz" element={<ProtectedRoute><BoundaryQuizPage /></ProtectedRoute>} />
                <Route path="/safety-boundaries-quiz" element={<ProtectedRoute><SafetyVsBoundariesQuizPage /></ProtectedRoute>} />
                <Route path="/quizzes" element={<ProtectedRoute><QuizzesPage /></ProtectedRoute>} />
                <Route path="/normalization-patterns" element={<ProtectedRoute><NormalizationPatternsPage /></ProtectedRoute>} />
                <Route path="/trauma-brain" element={<ProtectedRoute><TraumaBrainPage /></ProtectedRoute>} />
                <Route path="/trauma-bonding" element={<ProtectedRoute><TraumaBondingPage /></ProtectedRoute>} />
                <Route path="/post-separation" element={<ProtectedRoute><PostSeparationPage /></ProtectedRoute>} />
                <Route path="/healthy-relationships" element={<ProtectedRoute><HealthyRelationshipsPage /></ProtectedRoute>} />
                <Route path="/cutoff-guidance" element={<ProtectedRoute><CutoffGuidancePage /></ProtectedRoute>} />
                {/* Temporarily hidden per user request */}
                {/* <Route path="/divorce-misconceptions" element={<ProtectedRoute><DivorceMisconceptionsPage /></ProtectedRoute>} /> */}
                <Route path="/thought-fighting" element={<ProtectedRoute><ThoughtFightingPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/legal-privacy" element={<ProtectedRoute><LegalPrivacyPage /></ProtectedRoute>} />
                <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                <Route path="/admin/modules" element={<ProtectedRoute><ModuleAdminPage /></ProtectedRoute>} />
                <Route path="/admin/modules/reorder" element={<ProtectedRoute><ModuleReorderPage /></ProtectedRoute>} />
                <Route path="/admin/modules/new" element={<ProtectedRoute><ModuleFormPage /></ProtectedRoute>} />
                <Route path="/admin/modules/edit/:moduleId" element={<ProtectedRoute><ModuleFormPage /></ProtectedRoute>} />
                <Route path="/admin/sections" element={<ProtectedRoute><SectionManagerPage /></ProtectedRoute>} />
                <Route path="/admin/notification-cards" element={<ProtectedRoute><AdminNotificationCardsPage /></ProtectedRoute>} />
                <Route path="/admin/invite-links" element={<ProtectedRoute><AdminInviteLinksPage /></ProtectedRoute>} />
                <Route path="/admin/theme-customizer" element={<ProtectedRoute><AdminThemeCustomizerPage /></ProtectedRoute>} />
                <Route path="/my-insights" element={<ProtectedRoute><HighlightsSummaryPage /></ProtectedRoute>} />
                <Route path="/pricing" element={<ProtectedRoute><PricingSelectionPage /></ProtectedRoute>} />
                <Route path="/abuse-definition" element={<ProtectedRoute><AbuseDefinitionPage /></ProtectedRoute>} />
                <Route path="/burnout-recovery" element={<ProtectedRoute><BurnoutRecoveryPage /></ProtectedRoute>} />
                <Route path="/burnout-quiz" element={<ProtectedRoute><BurnoutQuizPage /></ProtectedRoute>} />
                <Route path="/trauma-screening-quiz" element={<ProtectedRoute><TraumaScreeningQuizPage /></ProtectedRoute>} />
                <Route path="/workplace-dynamics-quiz" element={<ProtectedRoute><WorkplaceDynamicsQuizPage /></ProtectedRoute>} />
                <Route path="/triggers-vs-threats" element={<ProtectedRoute><TriggersVsThreatsPage /></ProtectedRoute>} />
                <Route path="/window-of-tolerance" element={<ProtectedRoute><WindowOfTolerancePage /></ProtectedRoute>} />
                <Route path="/guided-mode" element={<ProtectedRoute><GuidedModePage /></ProtectedRoute>} />
                <Route path="/anxiety-body-suds" element={<ProtectedRoute><AnxietyBodySUDSPage /></ProtectedRoute>} />
                <Route path="/narcissistic-parent-teen-guide" element={<ProtectedRoute><NarcissisticParentTeenGuidePage /></ProtectedRoute>} />
                <Route path="/suds-chart-builder" element={<ProtectedRoute><SUDSChartBuilderPage /></ProtectedRoute>} />
                <Route path="/suds-summary" element={<ProtectedRoute><SUDSSummaryPage /></ProtectedRoute>} />
                <Route path="/body-map" element={<ProtectedRoute><BodyMapPage /></ProtectedRoute>} />
                <Route path="/signals-dictionary" element={<ProtectedRoute><SignalsDictionaryPage /></ProtectedRoute>} />
                <Route path="/over-labeling" element={<ProtectedRoute><OverLabelingPage /></ProtectedRoute>} />
                <Route path="/wallpaper-generator" element={<ProtectedRoute><WallpaperGeneratorPage /></ProtectedRoute>} />
                <Route path="/where-to-start" element={<ProtectedRoute><WhereToStartQuizPage /></ProtectedRoute>} />
                
                {/* Admin routes */}
                <Route path="/admin/notification-cards" element={<ProtectedRoute><AdminNotificationCardsPage /></ProtectedRoute>} />
                
                {/* Dynamic module viewer for database-driven modules */}
                <Route path="/module/:moduleRoute" element={<ProtectedRoute><DynamicModulePage /></ProtectedRoute>} />
              </Routes>
                  </Suspense>
                    </NotificationCardManager>
            </BrowserRouter>
              </ValuesProvider>
            </UserProgressProvider>
          </AuthProvider>
        </BackgroundWrapper>
      </ThemeProvider>
      <Toaster 
        position="top-center" 
        theme="system"
        toastOptions={{
          style: {
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--primary-blue)',
          },
          className: 'sonner-toast',
        }}
      />
    </div>
    </ErrorBoundary>
  );
}

export default App;
